import { FC, useMemo } from 'react';
import { Flex, Box } from '@rebass/emotion';
import { FormattedMessage } from 'react-intl';

import { isNotNullish } from '@app/helpers/predicate';
import Link from '@app/components/Link';
import Card from '@app/components/Card';
import Alerts from '@app/shop/components/Alerts';
import FormattedMoney from '@app/shop/components/FormattedMoney';

import { cmsBlockToAlertItem } from '@app/shop/helpers';
import { ParsedContentFormat } from '@app/shop/components/ParsedContent';
import { rem } from '@app/styles/theme/layout';
import { CheckoutSuccessQuery_magento2_cmsBlocks_items } from './__generated__/CheckoutSuccessQuery';
import {
  CheckoutOrderQuery_magento2_customer_globalOrders_items,
  CheckoutOrderQuery_magento2_storeConfig,
} from './__generated__/CheckoutOrderQuery';

import { SuccessTable } from './Success.styles';
import messages from './Success.messages';

const OrderReview: FC<{
  order: CheckoutOrderQuery_magento2_customer_globalOrders_items;
  notices?: CheckoutSuccessQuery_magento2_cmsBlocks_items[];
  storeConfig?: CheckoutOrderQuery_magento2_storeConfig;
}> = ({ order, notices, storeConfig }) => {
  const includingTax = storeConfig?.taxSalesDisplayPrice === 'including';
  const discounts = order.total?.discounts ?? null;
  const taxes = order.total?.taxes ?? null;
  const noticeAlerts = notices?.map((cmsBlock) =>
    cmsBlockToAlertItem({ cmsBlock, format: ParsedContentFormat.ParseReact })
  );

  const validDiscounts = useMemo(() => discounts?.filter(isNotNullish), [
    discounts,
  ]);

  const validTaxes = useMemo(() => taxes?.filter(isNotNullish), [taxes]);

  return (
    <Card>
      <SuccessTable>
        <thead>
          <tr>
            <td colSpan={3}>
              <FormattedMessage {...messages.order} />{' '}
              <span
                data-table="success-order-number"
                data-testid="order-number"
              >
                <FormattedMessage {...messages.number} />
                {order?.number}
              </span>
            </td>
          </tr>
        </thead>
        <tbody data-testid="order-item-list">
          {order.items?.map((item) => {
            if (item)
              return (
                <tr
                  key={item.productSku}
                  data-testid={`order-item-${item.productSku}`}
                >
                  <td>
                    {item.productImage && (
                      <img src={item.productImage} alt="" />
                    )}
                  </td>
                  <td colSpan={2}>
                    <Flex flexDirection="column" px={rem(5)}>
                      <h3>{item.productName}</h3>
                      <span>
                        <FormattedMessage {...messages.qty} />{' '}
                        {item.quantityOrdered} |{' '}
                        <span
                          data-testid={`success-order-${item.productSku}-price`}
                        >
                          {includingTax
                            ? item?.productSalePriceIncludingTax && (
                                <FormattedMoney
                                  {...item.productSalePriceIncludingTax}
                                />
                              )
                            : item?.productSalePrice && (
                                <FormattedMoney {...item.productSalePrice} />
                              )}
                        </span>
                      </span>
                    </Flex>
                  </td>
                </tr>
              );
          })}
          <tr data-table="success-simple-row" data-testid="order-subtotal">
            <td colSpan={2}>
              <FormattedMessage {...messages.orderSubtotal} />
            </td>
            <td
              data-table="success-simple-row-value"
              data-testid={'success-order-subtotal-value'}
            >
              {includingTax
                ? order.total?.subtotalIncludingTax && (
                    <FormattedMoney {...order.total?.subtotalIncludingTax} />
                  )
                : order.total?.subtotal && (
                    <FormattedMoney {...order.total?.subtotal} />
                  )}
            </td>
          </tr>
          {validDiscounts?.map(({ label, amount }) => (
            <tr
              key={label}
              data-table="success-simple-row"
              data-testid="order-discounts"
            >
              <td colSpan={2}>
                <FormattedMessage {...messages.discount} />
              </td>
              <td data-table="success-simple-row-value">
                <FormattedMoney
                  currency={amount.currency}
                  value={(amount.value ?? 0) * -1}
                />
              </td>
            </tr>
          ))}
          {validTaxes?.map(({ title, amount }) => (
            <tr
              key={title}
              data-table="success-simple-row"
              data-testid="order-taxes"
            >
              <td colSpan={2} data-testid={'success-order-tax-label'}>
                {includingTax ? (
                  <FormattedMessage {...messages.inclTax} />
                ) : (
                  <FormattedMessage {...messages.exTax} />
                )}
                {title ? ` (${title})` : null}
              </td>
              <td data-table="success-simple-row-value">
                <FormattedMoney {...amount} />
              </td>
            </tr>
          ))}
          {order.carrier && (
            <tr data-table="success-simple-row" data-testid="shipping-details">
              <td colSpan={2}>
                <FormattedMessage {...messages.shippingHandling} />
              </td>
              <td data-table="success-simple-row-value">
                {order.total?.totalShipping.value === 0 ? (
                  <span data-table="success-shipping-value-free">
                    <FormattedMessage {...messages.freeShipping} />
                  </span>
                ) : (
                  <span data-testId="success-shipping-handling-value">
                    <FormattedMoney
                      {...order.total?.shippingHandling?.[
                        includingTax
                          ? 'amountIncludingTax'
                          : 'amountExcludingTax'
                      ]}
                    />
                  </span>
                )}
              </td>
            </tr>
          )}
          <tr data-testid="order-total">
            <td colSpan={2}>
              <FormattedMessage {...messages.orderTotal} />
            </td>
            <td data-table="success-simple-row-value">
              <FormattedMoney {...order.total?.grandTotal} />
            </td>
          </tr>
          <tr data-table="success-order-details">
            <td colSpan={3}>
              <Link
                to={`/:language/account/orders/${order.number}`}
                data-testid="view-order-details-link"
              >
                <FormattedMessage {...messages.orderDetailsButton} />
              </Link>
            </td>
          </tr>
        </tbody>
      </SuccessTable>

      {!!noticeAlerts?.length && (
        <Box mt={20}>
          <Alerts alerts={noticeAlerts} />
        </Box>
      )}
    </Card>
  );
};

export default OrderReview;
